/**
 * What a feed row says about a job — shared by the list (`/feed`) and the job's own view
 * (`/feed/:id`), which `W4-T10` added. Moved out of `routes/feed.tsx` rather than copied: two
 * renderings of the licence label would be two places for it to drift.
 */
import { type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { type JobFeedItem } from '@marketplace/contracts';

/**
 * What the job asks for, and whether this provider lists it.
 *
 * The label that replaced a gate (`W4-T07` §2.2). It makes no claim about verification, because
 * nothing in this schema knows one — `W8` is unbuilt, and a badge here would be an invention.
 */
export function FeedCoverage({
  item,
  locale,
}: {
  item: JobFeedItem;
  locale: string;
}): ReactElement | null {
  const { t } = useTranslation();
  if (item.coverage.length === 0) return null;

  return (
    <>
      <p className="mp-meta">{t('feed.needs')}</p>
      <ul className="mp-meta">
        {item.coverage.map((entry) => (
          <li key={entry.slug}>
            {locale.startsWith('en') ? entry.nameEn : entry.nameEs}
            {' — '}
            {entry.listedByProvider ? t('feed.coverage.listed') : t('feed.coverage.notListed')}
            {entry.requiresLicence ? ` · ${t('feed.coverage.licence')}` : ''}
          </li>
        ))}
      </ul>
    </>
  );
}

/** The client's budget, when they gave one. Absent is the common case (`W4-T01`). */
export function FeedBudget({
  item,
  locale,
}: {
  item: JobFeedItem;
  locale: string;
}): ReactElement | null {
  const { t } = useTranslation();
  const { minCents, maxCents } = item.budget;
  if (minCents === null && maxCents === null) return null;

  const money = (cents: number): string =>
    (cents / 100).toLocaleString(locale, { style: 'currency', currency: 'EUR' });

  const range =
    minCents === null
      ? t('feed.budget.upTo', { max: money(maxCents as number) })
      : maxCents === null
        ? t('feed.budget.from', { min: money(minCents) })
        : `${money(minCents)} – ${money(maxCents)}`;

  return <p className="mp-meta">{t('feed.budget', { range })}</p>;
}

/** Where, how far, how soon — one line, as the list has always rendered it. */
export function FeedWhere({ item, locale }: { item: JobFeedItem; locale: string }): ReactElement {
  const { t } = useTranslation();
  const km = (item.distanceMetres / 1000).toLocaleString(locale, { maximumFractionDigits: 1 });

  return (
    <p className="mp-meta">
      {item.location.city}
      {' · '}
      {t('feed.distance', { km })}
      {item.urgency === null ? '' : ` · ${t(`search.when.${item.urgency}`)}`}
    </p>
  );
}
